import { supabase } from '../supabase';
import type { EmailContactStatus } from '../../types/email.types';

/**
 * Se una persona ricevera' le mail di SLAB (campagne e sequenze), detto in
 * una parola. Serve fuori dal Mailing, dove non si sa cosa vuol dire "in
 * attesa": nella scheda del lead si vede subito se gli si puo' scrivere.
 */
export type StatoMail = 'accettate' | 'da_confermare' | 'non_accettate' | 'bloccate' | 'nessuna_email';

export const STATO_MAIL: Record<StatoMail, { label: string; spiegazione: string; badge: string; puntino: string }> = {
  accettate: {
    label: 'Mail accettate',
    spiegazione: 'Ha dato il consenso: riceve campagne e sequenze.',
    badge: 'bg-green-50 text-green-700 border-green-200',
    puntino: 'bg-green-500',
  },
  da_confermare: {
    label: 'Da confermare',
    spiegazione:
      'Ha messo la spunta nel modulo ma non ha ancora cliccato il link nella mail di conferma: finche\' non lo fa non riceve niente.',
    badge: 'bg-amber-50 text-amber-700 border-amber-200',
    puntino: 'bg-amber-500',
  },
  non_accettate: {
    label: 'Mail non accettate',
    spiegazione:
      'Non ha accettato le promozioni: non riceve campagne ne\' sequenze promozionali. Le informazioni sui corsi a cui e\' iscritto gli arrivano comunque.',
    badge: 'bg-neutral-100 text-neutral-600 border-neutral-200',
    puntino: 'bg-neutral-400',
  },
  bloccate: {
    label: 'Non riceve',
    spiegazione: 'Si e\' disiscritto, ha segnalato spam o l\'indirizzo rimbalza: non riceve niente.',
    badge: 'bg-red-50 text-red-700 border-red-200',
    puntino: 'bg-red-500',
  },
  nessuna_email: {
    label: 'Nessuna email',
    spiegazione: 'Senza indirizzo email non gli si puo\' scrivere.',
    badge: 'bg-neutral-100 text-neutral-500 border-neutral-200',
    puntino: 'bg-neutral-300',
  },
};

/** Lo stato del contatto nel Mailing tradotto in "ricevera' o no". */
export function statoMail(
  status: EmailContactStatus | null,
  richiestaInAttesa: boolean
): StatoMail {
  if (status === null) return 'nessuna_email';
  if (status === 'attivo') return 'accettate';
  if (status === 'in_attesa') return richiestaInAttesa ? 'da_confermare' : 'non_accettate';
  return 'bloccate';
}

// Stesso motivo di emailService: le tabelle email_* non sono ancora in
// database.types.ts.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

/** Legge dal Mailing lo stato di un indirizzo. */
export async function leggiStatoMail(email: string | null | undefined): Promise<StatoMail> {
  const indirizzo = email?.trim().toLowerCase();
  if (!indirizzo) return 'nessuna_email';

  const { data: contatto, error } = await db
    .from('email_contacts')
    .select('id, status')
    .eq('email_normalized', indirizzo)
    .maybeSingle();
  if (error) throw error;
  if (!contatto) return 'non_accettate';

  if (contatto.status !== 'in_attesa') return statoMail(contatto.status, false);

  // La richiesta parte dal modulo con la spunta: c'e' una riga nel registro
  // dei consensi, e manca solo il clic sulla mail di conferma.
  const { count, error: erroreRichiesta } = await db
    .from('email_consents')
    .select('id', { count: 'exact', head: true })
    .eq('contact_id', contatto.id)
    .eq('action', 'richiesta_opt_in');
  if (erroreRichiesta) throw erroreRichiesta;

  return statoMail('in_attesa', (count ?? 0) > 0);
}
